// src/pages/AdminLoginPage.js

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import Swal from 'sweetalert2';

// --- CSS & ASSETS ---
// Import the styles from your beautiful user login page
import '../components/LoginSignup/LoginSignup.css'; // Adjust path if needed
import loginImage from '../assets/img/101.png'; // Adjust path if needed

// --- REUSABLE UI COMPONENTS (Taken from your LoginSignup component) ---
const SpinnerIcon = React.memo(() => <svg className="nfc-contact-spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>);

const CtaButton = React.memo(({ children, isLoading, disabled, ...props }) => (
    <motion.button 
        className="nfc-contact-cta-button" 
        whileHover={{ y: -3, boxShadow: "0 7px 20px rgba(0, 82, 212, 0.25)" }} 
        whileTap={{ y: -1, scale: 0.98 }} 
        transition={{ type: "spring", stiffness: 400, damping: 17 }} 
        disabled={isLoading || disabled} 
        {...props}>
            {isLoading ? <SpinnerIcon /> : children}
    </motion.button>
));

const InputField = React.memo(({ id, label, type, value, onChange, isRequired = true }) => (
    <div className="nfc-contact-input-group">
        <input 
            type={type} 
            id={id} 
            name={id} 
            value={value} 
            onChange={onChange} 
            placeholder=" " 
            className="nfc-contact-input-field" 
            required={isRequired} 
            autoComplete={type === 'password' ? 'current-password' : 'username'} 
        />
        <label htmlFor={id} className="nfc-contact-input-label">{label}</label>
    </div>
));


// --- MAIN ADMIN LOGIN COMPONENT ---
const AdminLoginPage = () => {
    // --- STATE MANAGEMENT ---
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    
    // --- HOOKS ---
    const auth = useAuth();
    const navigate = useNavigate();
    
    // Redirect away if already logged in
    if (auth.isAdmin) {
        navigate('/dash');
    }

    // --- FORM ACTIONS ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        
        const result = await auth.login(username, password);

        if (result.success) {
            Swal.fire({ 
                icon: 'success', 
                title: 'Login Successful!', 
                text: `Welcome, Admin!`,
                timer: 1500,
                showConfirmButton: false
            });
            // The redirection is handled by the AuthProvider now, but we can still push it
            navigate('/dash');
        } else {
            Swal.fire({ 
                icon: 'error', 
                title: 'Login Failed', 
                text: result.error || 'Invalid credentials.' 
            });
            setIsLoading(false);
        }
    };

    // Animation variants for Framer Motion
    const formVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } },
        exit: { opacity: 0, y: -20, transition: { duration: 0.3, ease: "easeIn" } }
    };

    return (
        <main className="nfc-contact-container">
            <div className="nfc-contact-background-container" />
            <div className="nfc-contact-page-container">
                <div className="nfc-contact-login-container">
                    
                    <div className="nfc-contact-brand-section">
                        <AnimatePresence>
                            <motion.img
                                key='admin-login-img'
                                src={loginImage}
                                alt="Admin Panel Illustration"
                                className="nfc-contact-welcome-image"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.5, ease: 'easeInOut' }}
                            />
                        </AnimatePresence>
                    </div>

                    <div className="nfc-contact-form-section">
                        <AnimatePresence>
                            <motion.div className="nfc-contact-form-content-wrapper" key="admin-login-step" variants={formVariants} initial="hidden" animate="visible" exit="exit">
                                <h1 className="nfc-contact-form-greeting">Admin Panel</h1>
                                <p className="nfc-contact-form-subtitle">Please sign in to continue.</p>
                                
                                <form onSubmit={handleSubmit} noValidate>
                                    <InputField 
                                        id="username" 
                                        label="Username" 
                                        type="text" 
                                        value={username} 
                                        onChange={(e) => setUsername(e.target.value)} 
                                    />
                                    <InputField 
                                        id="password" 
                                        label="Password" 
                                        type="password" 
                                        value={password} 
                                        onChange={(e) => setPassword(e.target.value)} 
                                    />
                                    <div className="nfc-contact-form-submit-group">
                                        <CtaButton type="submit" isLoading={isLoading}>Sign In</CtaButton>
                                    </div>
                                </form>
                            </motion.div>
                        </AnimatePresence>
                    </div>

                </div>
            </div>
        </main>
    );
};

export default AdminLoginPage;